import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import {
  calculateWordCount,
  determineMode,
  validateChangeThreshold,
  formatAnalysisForDisplay,
  saveStrategicAnalysis
} from '../../../services/StrategicWordCountService';
import AnalysisLoader from './AnalysisLoader';

export default function StrategicWordCountPanel({
  essay,
  essayContent,
  onClose
}) {

  // State
  const [targetCount, setTargetCount] = useState(essay?.word_limit || 650);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [currentWordCount, setCurrentWordCount] = useState(0);

  // Calculate current word count
  useEffect(() => {
    if (essayContent) {
      const count = calculateWordCount(essayContent);
      setCurrentWordCount(count);
    }
  }, [essayContent]);

  // Auto-analyze on mount if threshold is met
  useEffect(() => {
    if (essayContent && targetCount) {
      const validation = validateChangeThreshold(currentWordCount, targetCount);
      if (validation.isValid && !analysis) {
        handleAnalyze();
      }
    }
  }, [currentWordCount, targetCount]);

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    setError(null);

    try {
      // Validate essay content
      if (!essayContent || essayContent.trim().length < 100) {
        throw new Error('Essay content is too short for strategic analysis');
      }

      // Validate change threshold (50 words minimum)
      const validation = validateChangeThreshold(currentWordCount, targetCount, 50);
      if (!validation.isValid) {
        setError(validation.message);
        setIsAnalyzing(false);
        return;
      }

      // Determine mode
      const mode = determineMode(currentWordCount, targetCount);

      // Call API
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'strategic-word-count',
          essayContent: essayContent.trim(),
          currentCount: currentWordCount,
          targetCount,
          mode,
          essayId: essay?.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate strategic analysis');
      }

      // Format for display
      const formattedAnalysis = formatAnalysisForDisplay(data);
      setAnalysis(formattedAnalysis);

      // Save to database
      if (essay?.id) {
        await saveStrategicAnalysis(supabase, essay.id, data);
      }

    } catch (err) {
      console.error('Error in strategic word count analysis:', err);
      setError(err.message || 'Failed to generate analysis');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleTargetChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      setTargetCount(value);
    }
  };

  const mode = determineMode(currentWordCount, targetCount);
  const changeNeeded = Math.abs(targetCount - currentWordCount);
  const isOverTarget = currentWordCount > targetCount;

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">
            Strategic {mode === 'reduce' ? 'Reduction' : 'Expansion'}
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Word Count Display */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div className="text-sm text-slate-600 mb-1">Current</div>
            <div className="text-2xl font-bold text-slate-900">{currentWordCount}</div>
            <div className="text-xs text-slate-500">words</div>
          </div>

          <div className="text-center">
            <div className="text-sm text-slate-600 mb-1">Target</div>
            <input
              type="number"
              value={targetCount}
              onChange={handleTargetChange}
              className="text-2xl font-bold text-center w-full border-b-2 border-teal-500 focus:border-teal-600 outline-none text-slate-900"
              min="100"
              max="2000"
            />
            <div className="text-xs text-slate-500">words (editable)</div>
          </div>

          <div className="text-center">
            <div className="text-sm text-slate-600 mb-1">Change</div>
            <div className={`text-2xl font-bold ${isOverTarget ? 'text-coral-600' : 'text-teal-600'}`}>
              {isOverTarget ? '-' : '+'}{changeNeeded}
            </div>
            <div className="text-xs text-slate-500">words needed</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative h-3 bg-stone-100 rounded-full overflow-hidden">
          <div
            className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${
              isOverTarget
                ? 'bg-gradient-to-r from-coral-500 to-coral-600'
                : 'bg-gradient-to-r from-teal-500 to-teal-600'
            }`}
            style={{
              width: `${Math.min((currentWordCount / targetCount) * 100, 100)}%`
            }}
          />
        </div>

        {/* Re-analyze Button */}
        {analysis && !isAnalyzing && (
          <button
            onClick={handleAnalyze}
            className="mt-4 w-full px-4 py-2 bg-teal-50 text-teal-700 font-medium rounded-lg hover:bg-teal-100 transition-colors"
          >
            Re-analyze with New Target
          </button>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {isAnalyzing && (
          <div className="flex items-center justify-center h-full">
            <AnalysisLoader />
          </div>
        )}

        {error && (
          <div className="bg-coral-50 border border-coral-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-coral-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <div className="font-medium text-coral-900">Analysis Error</div>
                <div className="text-sm text-coral-700 mt-1">{error}</div>
              </div>
            </div>
          </div>
        )}

        {!isAnalyzing && !error && analysis && (
          <AnalysisDisplay analysis={analysis} mode={mode} />
        )}

        {!isAnalyzing && !error && !analysis && changeNeeded < 50 && (
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-6 text-center">
            <svg className="w-12 h-12 text-stone-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-lg font-medium text-slate-900 mb-2">
              Change Too Small for Strategic Editing
            </div>
            <div className="text-sm text-slate-600">
              You only need to adjust by {changeNeeded} words. Strategic editing is designed for meaningful changes (50+ words).
            </div>
            <div className="text-sm text-slate-600 mt-2">
              For small adjustments, use line-level editing instead.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AnalysisDisplay({ analysis, mode }) {
  if (!analysis || !analysis.sections) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Strategic Guidance */}
      {analysis.strategicGuidance && (
        <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded-r-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium text-indigo-900 mb-1">Strategic Guidance</div>
              <div className="text-sm text-indigo-800">{analysis.strategicGuidance}</div>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      {analysis.summary && (
        <div className="bg-white border border-stone-200 rounded-lg p-4">
          <div className="text-sm font-medium text-slate-900 mb-3">Summary</div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {mode === 'reduce' ? (
              <>
                <div>
                  <div className="text-slate-600">Potential Cuts</div>
                  <div className="text-lg font-bold text-coral-600">
                    {analysis.summary.potentialCuts} words
                  </div>
                </div>
                <div>
                  <div className="text-slate-600">Projected Count</div>
                  <div className="text-lg font-bold text-teal-600">
                    {analysis.summary.projectedCount} words
                  </div>
                </div>
                <div>
                  <div className="text-slate-600">Major Cuts</div>
                  <div className="text-lg font-bold text-slate-900">
                    {analysis.summary.majorCuts} sections
                  </div>
                </div>
                <div>
                  <div className="text-slate-600">Moderate Cuts</div>
                  <div className="text-lg font-bold text-slate-900">
                    {analysis.summary.moderateCuts} sections
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <div className="text-slate-600">Potential Additions</div>
                  <div className="text-lg font-bold text-teal-600">
                    +{analysis.summary.potentialAdditions} words
                  </div>
                </div>
                <div>
                  <div className="text-slate-600">Projected Count</div>
                  <div className="text-lg font-bold text-teal-600">
                    {analysis.summary.projectedCount} words
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-slate-600">Expansion Opportunities</div>
                  <div className="text-lg font-bold text-slate-900">
                    {analysis.summary.expansionOpportunities} sections
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Section Analysis */}
      <div className="space-y-4">
        <div className="text-sm font-medium text-slate-900">
          Section-by-Section Analysis ({analysis.sections.length} sections)
        </div>

        {analysis.sections.map((section) => (
          <SectionCard key={section.id} section={section} mode={mode} />
        ))}
      </div>
    </div>
  );
}

function SectionCard({ section, mode }) {
  const borderColor = {
    teal: 'border-teal-500',
    coral: 'border-coral-500',
    stone: 'border-stone-300'
  }[section.statusColor] || 'border-stone-300';

  const badgeColor = {
    teal: 'bg-teal-50 border-teal-200 text-teal-900',
    coral: 'bg-coral-50 border-coral-200 text-coral-900',
    stone: 'bg-stone-50 border-stone-200 text-stone-900'
  }[section.statusColor] || 'bg-stone-50 border-stone-200 text-stone-900';

  const impactColor = section.impact < 0 ? 'text-coral-600' : 'text-teal-600';

  return (
    <div className={`bg-white border-2 ${borderColor} rounded-lg p-4 hover:shadow-md transition-shadow`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-slate-900">{section.title}</h3>
            <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${badgeColor}`}>
              {section.status}
            </span>
          </div>
          <div className="text-xs text-slate-600">
            Paragraphs {section.startPara}
            {section.endPara !== section.startPara && `–${section.endPara}`} • {section.wordCount} words
          </div>
        </div>

        {section.impact && (
          <div className="text-right ml-4">
            <div className={`text-2xl font-bold ${impactColor}`}>
              {section.impactText}
            </div>
            <div className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${
              section.priority.includes('cut') ? 'bg-coral-100 text-coral-800' : 'bg-teal-100 text-teal-800'
            }`}>
              {section.priorityLabel}
            </div>
          </div>
        )}
      </div>

      {/* Analysis */}
      <div className="space-y-2 text-sm">
        <div className="text-slate-700">
          {section.analysis}
        </div>

        {section.action && (
          <div className="font-medium text-slate-900">
            <span className="text-slate-600">Action:</span> {section.action}
          </div>
        )}

        {section.suggestion && (
          <div className="bg-stone-50 border border-stone-200 rounded p-3 mt-2">
            <div className="text-xs font-medium text-slate-700 mb-1">Suggestion</div>
            <div className="text-slate-800 leading-relaxed">
              {section.suggestion}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
